import { Accordion } from '@zillow/constellation-10';
import React from 'react';

const FAQItemIndexContext = React.createContext(undefined);

const FAQItem = React.forwardRef(
    ({ as: Component, value, children, ...props }, ref) => {
        const autoIndex = React.useContext(FAQItemIndexContext);
        const itemValue = value ?? String(autoIndex ?? '');

        const itemCss = {
            borderWidth: '0',
            borderBottomWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.muted',
            borderRadius: '0',
            marginBottom: '0',
        };

        if (Component) {
            return (
                <Accordion.Item
                    ref={ref}
                    asChild
                    value={itemValue}
                    css={itemCss}
                    {...props}
                >
                    <Component>{children}</Component>
                </Accordion.Item>
            );
        }

        return (
            <Accordion.Item
                ref={ref}
                value={itemValue}
                css={itemCss}
                {...props}
            >
                {children}
            </Accordion.Item>
        );
    }
);
FAQItem.displayName = 'FAQ.Item';

const FAQHeader = React.forwardRef(
    ({ as: Component, children, ...props }, ref) => {
        // data-c11n-component selectors target internal Constellation elements.
        // Verified against @zillow/constellation-10@10.9.2:
        // - "Icon" is set by the Icon component wrapping the chevron in Accordion.Header
        // - "Heading" / "Text" are set by those components on their root element
        const headerCss = {
            borderWidth: '0',
            backgroundColor: 'transparent',
            flexDirection: 'row-reverse',
            paddingInline: '0',
            _hover: {
                backgroundColor: 'transparent',
                '& [data-c11n-component="Heading"], & [data-c11n-component="Text"]':
                    {
                        textDecoration: 'none',
                    },
            },
            _active: {
                backgroundColor: 'transparent',
                '& [data-c11n-component="Heading"], & [data-c11n-component="Text"]':
                    {
                        textDecoration: 'none',
                    },
            },
            '& [data-c11n-component="Icon"]': {
                color: 'text.accent.brand.hero',
            },
        };

        if (Component) {
            return (
                <Accordion.Header ref={ref} asChild css={headerCss} {...props}>
                    <Component>{children}</Component>
                </Accordion.Header>
            );
        }

        return (
            <Accordion.Header ref={ref} css={headerCss} {...props}>
                {children}
            </Accordion.Header>
        );
    }
);
FAQHeader.displayName = 'FAQ.Header';

const FAQPanel = React.forwardRef(
    ({ as: Component, children, ...props }, ref) => {
        const panelCss = {
            backgroundColor: 'transparent',
            borderTopWidth: '0',
            paddingInline: '0',
        };

        if (Component) {
            return (
                <Accordion.Panel ref={ref} asChild css={panelCss} {...props}>
                    <Component>{children}</Component>
                </Accordion.Panel>
            );
        }

        return (
            <Accordion.Panel ref={ref} css={panelCss} {...props}>
                {children}
            </Accordion.Panel>
        );
    }
);
FAQPanel.displayName = 'FAQ.Panel';

const toStringValues = (val) => {
    if (val === undefined || val === null) return undefined;
    if (Array.isArray(val)) return val.map((i) => String(i));
    return String(val);
};

const FAQ = React.forwardRef(
    (
        {
            expanded,
            defaultExpanded,
            onChange,
            // Legacy styled-components API: consumers passed forwardedAs to override the root element
            forwardedAs,
            children,
            ...props
        },
        ref
    ) => {
        const v10Expanded = React.useMemo(
            () => toStringValues(expanded),
            [expanded]
        );
        const v10DefaultExpanded = React.useMemo(
            () => toStringValues(defaultExpanded),
            [defaultExpanded]
        );

        const handleExpandedChange = React.useCallback(
            (newValue) => {
                if (!onChange) return;

                const newValues = Array.isArray(newValue)
                    ? newValue
                    : newValue != null
                    ? [newValue]
                    : [];
                const newIndices = newValues.map((v) => Number(v));
                const oldIndices = Array.isArray(expanded) ? expanded : [];

                const opened = newIndices.find((i) => !oldIndices.includes(i));
                const closed = oldIndices.find((i) => !newIndices.includes(i));
                const toggledIndex = opened !== undefined ? opened : closed;

                onChange(toggledIndex, newIndices);
            },
            [onChange, expanded]
        );

        const wrappedChildren = React.Children.map(children, (child, index) => (
            <FAQItemIndexContext.Provider value={index}>
                {child}
            </FAQItemIndexContext.Provider>
        ));

        const rootProps = {
            ref,
            ...(v10Expanded !== undefined && { expanded: v10Expanded }),
            ...(v10DefaultExpanded !== undefined && {
                defaultExpanded: v10DefaultExpanded,
            }),
            onExpandedChange: onChange ? handleExpandedChange : undefined,
            css: { gap: '0' },
            ...props,
        };

        if (forwardedAs) {
            const RootElement = forwardedAs;
            return (
                <Accordion.Root asChild {...rootProps}>
                    <RootElement>{wrappedChildren}</RootElement>
                </Accordion.Root>
            );
        }

        return (
            <Accordion.Root {...rootProps}>{wrappedChildren}</Accordion.Root>
        );
    }
);
FAQ.displayName = 'FAQ';

FAQ.Item = FAQItem;
FAQ.Header = FAQHeader;
FAQ.Panel = FAQPanel;

export default FAQ;
